import { useCallback, useState } from 'react';

export type TUseAsyncCallbackReturn<Data, Params> = {
  data: Data | null;
  isLoading: boolean;
  error: any;
  exec: (arg?: Params) => Promise<Data>;
  resetError: () => void;
  resetData: () => void;
  reset: () => void;
};

export type TUserAsyncCallbackOptions<Data> = {
  throwOnError?: boolean;
  onSuccess?: (data: Data) => void;
};

export function useAsyncCallback<Data, Params>(
  asyncFunction: (arg: Params) => Promise<Data>,
  options: TUserAsyncCallbackOptions<Data> = {}
): TUseAsyncCallbackReturn<Data, Params> {
  const [error, setError] = useState<unknown | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<Data | null>(null);

  const exec = useCallback(
    async (arg: Params) => {
      setError(null);
      setIsLoading(true);
      setData(null);

      try {
        const response = await asyncFunction(arg);
        setData(response);
        setIsLoading(false);
        setError(null);

        if (options.onSuccess) {
          options.onSuccess(response);
        }

        return response;
      } catch (e) {
        setError(e);
        setIsLoading(false);

        if (options.throwOnError) {
          throw e;
        } else {
          return null as Data;
        }
      }
    },
    [asyncFunction]
  );

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  const resetData = useCallback(() => {
    setData(null);
  }, []);

  const reset = useCallback(() => {
    resetError();
    resetData();
  }, []);

  return {
    data,
    isLoading,
    reset,
    error,
    exec,
    resetError,
    resetData
  };
}
